import React, { useState, useMemo } from 'react';
import { Search, Phone, MapPin, Plus, X, Edit3, Eye, Clock, DollarSign, FileText, Wrench, Calendar, Package, Trash2, AlertTriangle, AlertCircle, Mail } from 'lucide-react';
import { parseISO, parse } from 'date-fns';
import { formatHHMM } from '../utils/dateI18n';
import { normalizePhone } from '../utils/phoneUtils';
import { PaymentMethod, UserRole } from '../types/index';
import { UNIQUE_COUNTRIES } from '../utils/countries';
import { traduzirMetodoPagamento, dividaPendenteMovimento, movimentoTemPendente } from '../shared/logica/pagamentos';

// Helper para parsear datas de forma segura
const parseDate = (dateStr) => {
  if (!dateStr) return new Date();
  
  try {
    if (typeof dateStr === 'string') {
      // Se for formato ISO (com T) - prioridade para timestamps completos
      if (dateStr.includes('T')) {
        const parsed = parseISO(dateStr);
        if (!isNaN(parsed.getTime())) return parsed;
      }
      // Se for formato yyyy-MM-dd
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
        if (!isNaN(parsed.getTime())) return parsed;
      }
      // Tentar new Date genérico
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) return parsed;
    } else if (dateStr instanceof Date) {
      if (!isNaN(dateStr.getTime())) return dateStr;
    } else if (typeof dateStr === 'number') {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) return parsed;
    }
  } catch (e) {
    console.warn('Erro ao parsear data:', dateStr, e);
  }
  
  // Fallback: retornar data atual
  return new Date();
};

export default function ClientsView({ company, clients, movements, techJobs, onAddClient, onUpdateClient, onDeleteClient, currentUser }) {
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [clientDraft, setClientDraft] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [onlyDebts, setOnlyDebts] = useState(false);
  const [countryCode, setCountryCode] = useState('351'); // Padrão: Portugal
  const [editCountryCode, setEditCountryCode] = useState('351'); // Para edição
  const [editCountryCode2, setEditCountryCode2] = useState('351'); // Para edição phone2
  
  const isAdmin = currentUser?.role === UserRole.ADMIN;

  // Atualizar selectedClient quando clients mudarem (para refletir atualizações de dívida)
  React.useEffect(() => {
    if (selectedClient) {
      const updated = clients.find(c => c.id === selectedClient.id);
      if (updated) {
        setSelectedClient(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, selectedClient?.id]);

  // Calcular dívida pendente real para cada cliente (incluindo pagamentos mistos)
  const clientsWithPendingDebt = useMemo(() => {
    return clients.map(client => {
      // Buscar movimentos do cliente
      const clientPhones = [
        normalizePhone(client.phone),
        normalizePhone(client.phone2),
        normalizePhone(client.phone3)
      ].filter(p => p && p.length >= 6);
      
      const isConsumidorFinal = client.name === 'Consumidor Final' || client.specialId === '160';
      
      const clientMovements = movements.filter(m => {
        if (isConsumidorFinal) {
          return m.saleType === 'SIMPLE' && m.companyId === company.id;
        }
        
        const movementPhone = normalizePhone(m.phone);
        const matchesPhone = movementPhone && clientPhones.some(cp => cp === movementPhone);
        
        return matchesPhone && m.companyId === company.id;
      });
      
      // Calcular dívida pendente real dos movimentos (incluindo pagamentos mistos)
      const calculatedPendingDebt = clientMovements.reduce((sum, m) => sum + dividaPendenteMovimento(m), 0);
      
      // Priorizar dívida calculada dos movimentos (sempre atualizada) sobre totalDebt armazenado
      // O totalDebt armazenado é atualizado assincronamente, então a dívida calculada é mais precisa
      // Usar o maior valor apenas como fallback para garantir que não perdemos dívidas antigas
      // Mas se calculatedPendingDebt for 0, significa que não há dívidas pendentes nos movimentos
      const totalDebtWithPending = calculatedPendingDebt > 0 
        ? Math.max(client.totalDebt || 0, calculatedPendingDebt)
        : calculatedPendingDebt; // Se calculado é 0, usar 0 (dívida paga)
      
      return {
        ...client,
        calculatedPendingDebt,
        totalDebtWithPending,
        hasPending: calculatedPendingDebt > 0
      };
    });
  }, [clients, movements, company]);

  const filtered = clientsWithPendingDebt.filter(c => {
    // Filtrar por dívidas se o filtro estiver ativo
    if (onlyDebts && c.totalDebtWithPending <= 0) {
      return false;
    }
    
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || 
           (c.locality || '').toLowerCase().includes(s) ||
           (c.phone || '').includes(s) || 
           (c.phone2 || '').includes(s) ||
           (c.nif || '').includes(s) ||
           (c.email || '').toLowerCase().includes(s);
  }).sort((a,b) => b.totalDebtWithPending - a.totalDebtWithPending);

  const openClient = (client) => {
    setSelectedClient(client);
    setIsEditingClient(false);
    setClientDraft(null);
  };

  const openClientForEdit = (client) => {
    if (!client) return;
    setSelectedClient(client);
    setClientDraft({
      name: client.name || '',
      nif: client.nif || '',
      phone: client.phone || '',
      phone2: client.phone2 || '',
      email: client.email || '',
      street: client.street || '',
      doorNumber: client.doorNumber || '',
      locality: client.locality || '',
      zipCode: client.zipCode || ''
    });
    setIsEditingClient(true);
  };

  const startEditClient = (client = selectedClient) => {
    if (!client) return;
    
    // Extrair código do país do telefone se existir
    const extractCountryCode = (phone) => {
      if (!phone) return '351';
      const digits = phone.replace(/\D/g, '');
      if (digits.startsWith('00')) {
        // 00351 -> 351
        if (digits.startsWith('00351')) return '351';
        if (digits.startsWith('0049')) return '49';
        if (digits.startsWith('0033')) return '33';
        if (digits.startsWith('0031')) return '31';
        // Adicionar mais conforme necessário
      }
      if (phone.startsWith('+351') || digits.startsWith('351')) return '351';
      if (phone.startsWith('+49') || digits.startsWith('49')) return '49';
      if (phone.startsWith('+33') || digits.startsWith('33')) return '33';
      if (phone.startsWith('+31') || digits.startsWith('31')) return '31';
      return '351'; // Padrão
    };
    
    const phoneCountryCode = extractCountryCode(client.phone);
    const phone2CountryCode = extractCountryCode(client.phone2);
    
    setEditCountryCode(phoneCountryCode);
    setEditCountryCode2(phone2CountryCode);
    
    // Remover código do país do telefone para exibição no campo
    const cleanPhone = (phone, code) => {
      if (!phone) return '';
      const normalized = normalizePhone(phone, code);
      return normalized;
    };
    
    setClientDraft({
      name: client.name || '',
      nif: client.nif || '',
      phone: cleanPhone(client.phone, phoneCountryCode),
      phone2: cleanPhone(client.phone2, phone2CountryCode),
      email: client.email || '',
      street: client.street || '',
      doorNumber: client.doorNumber || '',
      locality: client.locality || '',
      zipCode: client.zipCode || ''
    });
    setIsEditingClient(true);
  };

  const discardEditClient = () => {
    setIsEditingClient(false);
    setClientDraft(null);
  };

  const saveClientDraft = (e) => {
    e.preventDefault();
    if (!selectedClient || !clientDraft) return;
    
    // Formatar telefones com código do país
    const formatPhoneWithCountry = (phone, countryCode) => {
      if (!phone) return '';
      if (phone.startsWith('+') || phone.startsWith('00')) return phone;
      return `+${countryCode}${phone}`;
    };
    
    const updated = { 
      ...selectedClient, 
      ...clientDraft,
      phone: formatPhoneWithCountry(clientDraft.phone, editCountryCode),
      phone2: formatPhoneWithCountry(clientDraft.phone2, editCountryCode2)
    };
    onUpdateClient(updated);
    setSelectedClient(updated);
    setIsEditingClient(false);
    setClientDraft(null);
    setEditCountryCode('351');
    setEditCountryCode2('351');
  };

  const handleDeleteClient = async (clientId) => {
    if (!onDeleteClient || !clientId) return;
    await onDeleteClient(clientId);
    setSelectedClient(null);
    setShowDeleteConfirm(false);
    discardEditClient();
  };

  // Filtrar movimentos relacionados ao cliente selecionado
  const clientMovements = useMemo(() => {
    if (!selectedClient) return [];
    
    const clientPhones = [
      normalizePhone(selectedClient.phone),
      normalizePhone(selectedClient.phone2),
      normalizePhone(selectedClient.phone3)
    ].filter(p => p && p.length >= 6);
    
    const isConsumidorFinal = selectedClient.name === 'Consumidor Final' || selectedClient.specialId === '160';
    
    return movements.filter(m => {
      // Para Consumidor Final, verificar se é venda simples
      if (isConsumidorFinal) {
        return m.saleType === 'SIMPLE' && m.companyId === company.id;
      }
      
      // Para outros clientes, verificar APENAS por telefone (não por nome)
      const movementPhone = normalizePhone(m.phone);
      const matchesPhone = movementPhone && clientPhones.some(cp => cp === movementPhone);
      
      return matchesPhone && m.companyId === company.id;
    });
  }, [selectedClient, movements, company]);

  // Filtrar assistências técnicas relacionadas ao cliente
  const clientTechJobs = useMemo(() => {
    if (!selectedClient) return [];
    
    const clientPhones = [
      normalizePhone(selectedClient.phone),
      normalizePhone(selectedClient.phone2),
      normalizePhone(selectedClient.phone3)
    ].filter(p => p && p.length >= 6);
    
    return techJobs.filter(tj => {
      // Verificar APENAS por telefone (não por nome)
      const techPhone = normalizePhone(tj.phone);
      const matchesPhone = techPhone && clientPhones.some(cp => cp === techPhone);
      
      return matchesPhone && tj.companyId === company.id && !tj.isArchived;
    });
  }, [selectedClient, techJobs, company]);

  // Calcular pendente financeiro (dívidas pendentes)
  const pendingDebt = useMemo(() => {
    return clientMovements
      .filter(m => movimentoTemPendente(m))
      .reduce((sum, m) => sum + dividaPendenteMovimento(m), 0);
  }, [clientMovements]);

  // Criar timeline cronológica combinada
  const timeline = useMemo(() => {
    const items = [];
    
    // Adicionar movimentos
    clientMovements.forEach(m => {
      items.push({
        type: 'MOVEMENT',
        id: m.id,
        date: m.date,
        timestamp: m.createdAt || m.date,
        data: m,
        icon: Package,
        color: 'blue',
        title: `${m.type === 'VENDA' ? 'Venda' : m.type === 'COMPRA' ? 'Compra' : m.type}`,
        subtitle: m.product || m.client,
        value: m.value,
        paymentMethod: m.paymentMethod
      });
    });
    
    // Adicionar assistências técnicas
    clientTechJobs.forEach(tj => {
      items.push({
        type: 'TECH',
        id: tj.id,
        date: tj.faultDate || tj.createdAt,
        timestamp: tj.createdAt || tj.faultDate,
        data: tj,
        icon: Wrench,
        color: 'orange',
        title: 'Assistência Técnica',
        subtitle: `${tj.brand || ''} ${tj.reference || ''}`.trim() || 'Sem referência',
        status: tj.status
      });
    });
    
    // Ordenar por data (mais recente primeiro)
    return items.sort((a, b) => {
      const dateA = parseDate(a.timestamp || a.date);
      const dateB = parseDate(b.timestamp || b.date);
      return dateB - dateA;
    });
  }, [clientMovements, clientTechJobs]);

  return (
    <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8 px-4 sm:px-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
        <div>
           <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">Base de Clientes</h1>
           <p className="text-green-600 font-bold mt-1 uppercase text-[10px] tracking-widest">Gestão de Identidade e Localização</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <button 
            onClick={() => setOnlyDebts(!onlyDebts)}
            className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-black flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all text-sm sm:text-base ${
              onlyDebts 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            <AlertCircle size={18} className="sm:w-5 sm:h-5" /> <span className="whitespace-nowrap">Apenas Dívidas</span>
          </button>
          <button 
            onClick={() => setShowAddClient(true)}
            className="bg-[#1b5e20] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-black flex items-center justify-center gap-2 shadow-lg hover:bg-green-900 active:scale-95 transition-all text-sm sm:text-base"
          >
            <Plus size={18} className="sm:w-5 sm:h-5" /> <span className="whitespace-nowrap">Novo Cliente</span>
          </button>
        </div>
      </div>

      <div className="bg-white p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] shadow-xl border-2 border-slate-100 flex gap-4">
        <div className="relative flex-1 group">
          <Search className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-green-600 sm:w-6 sm:h-6" size={20} />
          <input 
            type="text" 
            placeholder="Pesquisar por Nome, Telefone ou NIF..." 
            className="w-full pl-12 sm:pl-16 pr-4 sm:pr-6 py-3 sm:py-5 bg-slate-50 rounded-2xl sm:rounded-3xl outline-none font-bold text-base sm:text-xl focus:bg-white transition-all shadow-inner"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filtered.map(client => (
          <div 
            key={client.id}
            className="bg-white p-6 sm:p-8 rounded-[2rem] sm:rounded-[3rem] shadow-xl border-2 border-transparent hover:border-green-500 transition-all cursor-pointer group relative overflow-hidden"
            onClick={() => openClient(client)}
          >
            <div className="flex items-center gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 text-green-800 rounded-full flex items-center justify-center font-black text-lg sm:text-2xl group-hover:bg-green-800 group-hover:text-white transition-all shadow-inner flex-shrink-0">
                {client.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-black text-slate-900 truncate">{client.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                   <p className={`text-[10px] font-black uppercase ${client.totalDebtWithPending > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {client.totalDebtWithPending > 0 ? `Dívida: ${client.totalDebtWithPending.toFixed(2)}€` : 'Saldado'}
                  </p>
                  <span className="text-slate-300">•</span>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">NIF: {client.nif || '---'}</span>
                </div>
              </div>
            </div>
            <div className="space-y-2 mb-4 sm:mb-6">
              {client.phone && <div className="flex items-center gap-2 sm:gap-3 text-slate-500 font-bold text-xs sm:text-sm"><Phone size={14} className="sm:w-4 sm:h-4 text-green-600 flex-shrink-0"/> <span className="truncate">{client.phone}</span></div>}
              {client.email && <div className="flex items-center gap-2 sm:gap-3 text-slate-500 font-bold text-xs sm:text-sm"><Mail size={14} className="sm:w-4 sm:h-4 text-purple-600 flex-shrink-0"/> <span className="truncate">{client.email}</span></div>}
              {client.locality && <div className="flex items-center gap-2 sm:gap-3 text-slate-500 font-bold text-xs sm:text-sm"><MapPin size={14} className="sm:w-4 sm:h-4 text-green-600 flex-shrink-0"/> <span className="truncate">{client.locality}</span></div>}
            </div>
            
            <div className="flex gap-2 pt-4 border-t border-slate-50">
               <button 
                 onClick={(e) => { e.stopPropagation(); openClient(client); }} 
                 className="flex-1 bg-slate-50 hover:bg-green-50 text-slate-400 hover:text-green-700 p-2 sm:p-3 rounded-xl sm:rounded-2xl transition-all flex items-center justify-center gap-1 sm:gap-2 font-black text-[9px] sm:text-[10px] uppercase"
               >
                 <Eye size={14} className="sm:w-4 sm:h-4" /> <span className="hidden sm:inline">Ver Tudo</span><span className="sm:hidden">Ver</span>
               </button>
               <button 
                 onClick={(e) => { e.stopPropagation(); openClientForEdit(client); }} 
                 className="flex-1 bg-slate-50 hover:bg-blue-50 text-slate-400 hover:text-blue-700 p-2 sm:p-3 rounded-xl sm:rounded-2xl transition-all flex items-center justify-center gap-1 sm:gap-2 font-black text-[9px] sm:text-[10px] uppercase"
               >
                 <Edit3 size={14} className="sm:w-4 sm:h-4" /> Editar
               </button>
            </div>
          </div>
        ))}
      </div>

      {showAddClient && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[130] flex items-center justify-center p-4">
            <form onSubmit={(e) => { 
              e.preventDefault(); 
              const phoneValue = e.target.cPhone.value;
              // Se o telefone não começa com +, adicionar código do país
              const formattedPhone = phoneValue.startsWith('+') || phoneValue.startsWith('00') 
                ? phoneValue 
                : `+${countryCode}${phoneValue}`;
              
              onAddClient({ 
                name: e.target.cName.value, 
                phone: formattedPhone,
                locality: e.target.cLoc.value,
                nif: e.target.cNif.value,
                email: e.target.cEmail.value,
                street: e.target.cStreet.value,
                doorNumber: e.target.cDoor.value,
                zipCode: e.target.cZip.value
              }); 
              setShowAddClient(false);
              setCountryCode('351'); // Reset
            }} className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-2xl shadow-2xl space-y-4 sm:space-y-6 max-h-[95vh] overflow-y-auto">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl sm:text-2xl font-black text-slate-900">Novo Cliente</h2>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowAddClient(false);
                      setCountryCode('351'); // Reset
                    }} 
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} className="sm:w-6 sm:h-6"/>
                  </button>
                </div>
                
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="col-span-1 md:col-span-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Nome Completo *</label>
                          <input name="cName" required className="w-full p-4 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none" />
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Telefone *</label>
                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-4">
                              <select
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-xs border-2 border-transparent focus:border-green-500 outline-none"
                              >
                                {UNIQUE_COUNTRIES.map(country => (
                                  <option key={country.code} value={country.code}>
                                    {country.flag} +{country.code}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-span-8">
                              <input 
                                name="cPhone" 
                                required 
                                className="w-full p-4 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none" 
                                placeholder={countryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">NIF</label>
                          <input name="cNif" className="w-full p-4 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none" />
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">E-mail <span className="text-[9px] normal-case font-normal">(opcional)</span></label>
                          <input name="cEmail" type="email" className="w-full p-4 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none" placeholder="exemplo@email.com" />
                        </div>
                    </div>

                    <div className="p-6 bg-slate-50 rounded-[2.5rem] space-y-4 border border-slate-200">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Localização e Morada</p>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="md:col-span-3">
                              <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Rua / Morada</label>
                              <input name="cStreet" className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500" />
                            </div>
                            <div>
                              <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Nº Porta</label>
                              <input name="cDoor" className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Localidade</label>
                              <input name="cLoc" className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Código Postal</label>
                              <input name="cZip" className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500" />
                            </div>
                        </div>
                    </div>
                </div>
                <button type="submit" className="w-full py-5 bg-[#1b5e20] text-white font-black rounded-2xl text-xl shadow-xl hover:bg-green-900 transition-all">Criar Ficha Completa</button>
            </form>
        </div>
      )}

      {selectedClient && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-xl z-[120] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white w-full max-w-5xl rounded-[2rem] sm:rounded-[3rem] shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
             <div className="p-4 sm:p-6 lg:p-10 bg-[#1b5e20] text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
                <div className="flex gap-3 sm:gap-6 items-center w-full sm:w-auto">
                   <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-white/20 rounded-full flex items-center justify-center text-2xl sm:text-3xl lg:text-4xl font-black flex-shrink-0">{selectedClient.name[0]}</div>
                   <div className="flex-1 min-w-0">
                      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight truncate">{selectedClient.name}</h2>
                      <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 opacity-80 font-bold text-xs sm:text-sm">
                         <span className="truncate">{selectedClient.phone || '—'}</span>
                         {selectedClient.email && (
                           <>
                             <span className="hidden sm:inline">•</span>
                             <span className="truncate flex items-center gap-1">
                               <Mail size={12} className="flex-shrink-0" />
                               {selectedClient.email}
                             </span>
                           </>
                         )}
                         <span className="hidden sm:inline">•</span>
                         <span className="truncate">NIF: {selectedClient.nif || 'N/A'}</span>
                      </div>
                   </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
                  {!isEditingClient ? (
                    <>
                      <button
                        type="button"
                        onClick={() => startEditClient(selectedClient)}
                        title="Editar"
                        aria-label="Editar"
                        className="p-3 sm:p-4 bg-white text-green-900 rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all"
                      >
                        <Edit3 size={18} className="sm:w-5 sm:h-5" />
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            // Always show confirmation dialog before deleting
                            setShowDeleteConfirm(true);
                          }}
                          title="Eliminar Cliente"
                          aria-label="Eliminar Cliente"
                          className="p-3 sm:p-4 bg-red-500 text-white rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all hover:bg-red-600"
                        >
                          <Trash2 size={18} className="sm:w-5 sm:h-5" />
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={discardEditClient}
                        title="Ver tudo"
                        aria-label="Ver tudo"
                        className="p-3 sm:p-4 bg-white text-green-900 rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all"
                      >
                        <Eye size={18} className="sm:w-5 sm:h-5" />
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          title="Eliminar Cliente"
                          aria-label="Eliminar Cliente"
                          className="p-3 sm:p-4 bg-red-500 text-white rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all hover:bg-red-600"
                        >
                          <Trash2 size={18} className="sm:w-5 sm:h-5" />
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => { setSelectedClient(null); discardEditClient(); setShowDeleteConfirm(false); }}
                    title="Fechar"
                    aria-label="Fechar"
                    className="p-3 sm:p-4 hover:bg-white/10 rounded-xl sm:rounded-2xl text-white transition-all"
                  >
                    <X size={20} className="sm:w-6 sm:h-6"/>
                  </button>
                </div>
             </div>
             <div className="p-4 sm:p-6 lg:p-10 flex-1 overflow-y-auto bg-slate-50">
                {!isEditingClient ? (
                  <>
                    {/* Cards de Resumo */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 border border-slate-100 shadow-sm relative">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <DollarSign className="text-red-600 sm:w-5 sm:h-5" size={16} />
                          </div>
                          <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest">PENDENTE FINANCEIRO</div>
                        </div>
                        <p className="text-lg sm:text-xl lg:text-2xl font-black text-slate-900">{pendingDebt.toFixed(2)}€</p>
                        {(selectedClient.name === 'Consumidor Final' || selectedClient.specialId === '160') && 
                         Math.abs((selectedClient.totalDebt || 0) - pendingDebt) > 0.01 && (
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-[9px] font-black text-amber-800 uppercase">Dívida armazenada: {(selectedClient.totalDebt || 0).toFixed(2)}€</p>
                            <p className="text-[8px] text-amber-700 mt-1">Clique no botão de atualizar para sincronizar</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="text-blue-600 sm:w-5 sm:h-5" size={16} />
                          </div>
                          <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest">NIF / CONTRIBUINTE</div>
                        </div>
                        <p className="text-sm sm:text-base lg:text-lg font-black text-slate-900 truncate">{selectedClient.nif || 'NÃO DEFINIDO'}</p>
                      </div>
                      
                      {selectedClient.email && (
                        <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 border border-slate-100 shadow-sm">
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Mail className="text-purple-600 sm:w-5 sm:h-5" size={16} />
                            </div>
                            <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest">E-MAIL</div>
                          </div>
                          <p className="text-sm sm:text-base lg:text-lg font-black text-slate-900 truncate">{selectedClient.email}</p>
                        </div>
                      )}
                      
                      <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Calendar className="text-green-600 sm:w-5 sm:h-5" size={16} />
                          </div>
                          <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest">REGISTOS TOTAIS</div>
                        </div>
                        <p className="text-lg sm:text-xl lg:text-2xl font-black text-slate-900">{clientMovements.length}</p>
                      </div>
                      
                      <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Wrench className="text-orange-600 sm:w-5 sm:h-5" size={16} />
                          </div>
                          <div className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest">ASSISTÊNCIAS</div>
                        </div>
                        <p className="text-lg sm:text-xl lg:text-2xl font-black text-slate-900">{clientTechJobs.length}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
                      <div className="lg:col-span-1 bg-white rounded-[2rem] sm:rounded-[2.5rem] p-4 sm:p-6 lg:p-8 border border-slate-100 shadow-sm">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                          <MapPin size={14} /> DADOS DE MORADA
                        </p>
                        <div className="space-y-4">
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">RUA / AVENIDA</p>
                            <p className="font-black text-slate-900 text-sm">{selectedClient.street || 'Não registado'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">N° PORTA</p>
                            <p className="font-black text-slate-900 text-sm">{selectedClient.doorNumber || 'Não registado'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">LOCALIDADE</p>
                            <p className="font-black text-slate-900 text-sm">{selectedClient.locality || 'Não registado'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">CÓD. POSTAL</p>
                            <p className="font-black text-slate-900 text-sm">{selectedClient.zipCode || 'Não registado'}</p>
                          </div>
                          <button
                            onClick={() => startEditClient(selectedClient)}
                            className="w-full mt-4 py-3 bg-green-50 hover:bg-green-100 text-green-800 font-black rounded-xl flex items-center justify-center gap-2 transition-all"
                          >
                            <Edit3 size={16} /> ATUALIZAR LOCALIZAÇÃO
                          </button>
                        </div>
                      </div>

                      <div className="lg:col-span-2 bg-white rounded-[2rem] sm:rounded-[2.5rem] p-4 sm:p-6 lg:p-8 border border-slate-100 shadow-sm">
                        <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 sm:mb-6 flex items-center gap-2">
                          <Clock size={12} className="sm:w-3.5 sm:h-3.5" /> Cronologia de Atividade
                        </p>
                        <div className="space-y-3 sm:space-y-4 max-h-[400px] sm:max-h-[500px] overflow-y-auto">
                          {timeline.length === 0 ? (
                            <div className="text-center py-12 text-slate-400">
                              <Clock size={48} className="mx-auto mb-4 opacity-50" />
                              <p className="font-bold">Sem histórico para este cliente.</p>
                            </div>
                          ) : (
                            timeline.map((item, index) => {
                              const Icon = item.icon;
                              const date = parseDate(item.timestamp || item.date);
                              
                              // Validar se a data é válida antes de formatar
                              let formattedDate = 'Data inválida';
                              let formattedTime = '';
                              
                              if (!isNaN(date.getTime())) {
                                try {
                                  formattedDate = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
                                  formattedTime = formatHHMM(date);
                                } catch (e) {
                                  console.warn('Erro ao formatar data:', date, e);
                                  formattedDate = date.toLocaleDateString('pt-PT');
                                  formattedTime = date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
                                }
                              }
                              
                              return (
                                <div key={`${item.type}-${item.id}`} className="flex gap-3 sm:gap-4 pb-3 sm:pb-4 border-b border-slate-100 last:border-0">
                                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                    item.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                                    item.color === 'orange' ? 'bg-orange-50 text-orange-600' :
                                    'bg-slate-50 text-slate-600'
                                  }`}>
                                    <Icon size={18} className="sm:w-5 sm:h-5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-2 sm:gap-4 mb-1">
                                      <div className="flex-1 min-w-0">
                                        <p className="font-black text-slate-900 text-sm sm:text-base">{item.title}</p>
                                        <p className="text-xs sm:text-sm text-slate-600 font-bold truncate">{item.subtitle}</p>
                                      </div>
                                      <div className="text-left sm:text-right flex-shrink-0">
                                        <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase">{formattedDate}</p>
                                        <p className="text-[10px] sm:text-xs text-slate-500">{formattedTime}</p>
                                      </div>
                                    </div>
                                    {item.type === 'MOVEMENT' && (
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className={`text-xs font-black px-2 py-1 rounded ${
                                          item.paymentMethod === PaymentMethod.CASH ? 'bg-emerald-50 text-emerald-600' :
                                          item.paymentMethod === PaymentMethod.CARD ? 'bg-blue-50 text-blue-600' :
                                          item.paymentMethod === PaymentMethod.VOUCHER ? 'bg-purple-50 text-purple-600' :
                                          item.paymentMethod === PaymentMethod.TRANSFER ? 'bg-indigo-50 text-indigo-600' :
                                          item.paymentMethod === PaymentMethod.CHEQUE ? 'bg-orange-50 text-orange-600' :
                                          item.paymentMethod === PaymentMethod.PENDING ? 'bg-red-50 text-red-600' :
                                          item.paymentMethod === PaymentMethod.MBWAY ? 'bg-cyan-50 text-cyan-600' :
                                          'bg-amber-50 text-amber-600'
                                        }`}>
                                          {traduzirMetodoPagamento(item.paymentMethod)}
                                        </span>
                                        <span className="font-black text-slate-900">{item.value?.toFixed(2)}€</span>
                                      </div>
                                    )}
                                    {item.type === 'TECH' && (
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className={`text-xs font-black px-2 py-1 rounded ${
                                          item.status === 'ABERTA' ? 'bg-orange-50 text-orange-600' :
                                          item.status === 'CONCLUIDA' ? 'bg-green-50 text-green-600' :
                                          'bg-slate-50 text-slate-600'
                                        }`}>
                                          {item.status}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <form onSubmit={saveClientDraft} className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Nome Completo</label>
                        <input
                          required
                          value={clientDraft?.name ?? ''}
                          onChange={e => setClientDraft(prev => ({ ...(prev || {}), name: e.target.value }))}
                          className="w-full p-5 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">NIF / Identificação Fiscal</label>
                        <input
                          value={clientDraft?.nif ?? ''}
                          onChange={e => setClientDraft(prev => ({ ...(prev || {}), nif: e.target.value }))}
                          className="w-full p-5 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">E-mail</label>
                        <input
                          type="email"
                          value={clientDraft?.email ?? ''}
                          onChange={e => setClientDraft(prev => ({ ...(prev || {}), email: e.target.value }))}
                          className="w-full p-5 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Contacto Telefónico</label>
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-4">
                            <select
                              value={editCountryCode}
                              onChange={(e) => setEditCountryCode(e.target.value)}
                              className="w-full p-5 bg-slate-50 rounded-2xl font-bold text-xs border-2 border-transparent focus:border-green-500 outline-none"
                            >
                              {UNIQUE_COUNTRIES.map(country => (
                                <option key={country.code} value={country.code}>
                                  {country.flag} +{country.code}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-8">
                            <input
                              value={clientDraft?.phone ?? ''}
                              onChange={e => setClientDraft(prev => ({ ...(prev || {}), phone: e.target.value }))}
                              className="w-full p-5 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none"
                              placeholder={editCountryCode === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Contacto Telefónico (2)</label>
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-4">
                            <select
                              value={editCountryCode2}
                              onChange={(e) => setEditCountryCode2(e.target.value)}
                              className="w-full p-5 bg-slate-50 rounded-2xl font-bold text-xs border-2 border-transparent focus:border-green-500 outline-none"
                            >
                              {UNIQUE_COUNTRIES.map(country => (
                                <option key={country.code} value={country.code}>
                                  {country.flag} +{country.code}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-8">
                            <input
                              value={clientDraft?.phone2 ?? ''}
                              onChange={e => setClientDraft(prev => ({ ...(prev || {}), phone2: e.target.value }))}
                              className="w-full p-5 bg-slate-50 rounded-2xl font-bold border-2 border-transparent focus:border-green-500 outline-none"
                              placeholder={editCountryCode2 === '351' ? 'Ex: 912 345 678' : 'Ex: 123456789'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 bg-slate-50 rounded-[2.5rem] space-y-4 border border-slate-200">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Edição de Morada</p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-3">
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Rua / Avenida / Travessa</label>
                          <input
                            value={clientDraft?.street ?? ''}
                            onChange={e => setClientDraft(prev => ({ ...(prev || {}), street: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Nº Porta</label>
                          <input
                            value={clientDraft?.doorNumber ?? ''}
                            onChange={e => setClientDraft(prev => ({ ...(prev || {}), doorNumber: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Localidade / Cidade</label>
                          <input
                            value={clientDraft?.locality ?? ''}
                            onChange={e => setClientDraft(prev => ({ ...(prev || {}), locality: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase block mb-1 px-2">Cód. Postal</label>
                          <input
                            value={clientDraft?.zipCode ?? ''}
                            onChange={e => setClientDraft(prev => ({ ...(prev || {}), zipCode: e.target.value }))}
                            className="w-full p-4 bg-white rounded-2xl font-bold border border-slate-200 outline-none focus:border-green-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-2">
                      <button
                        type="button"
                        onClick={discardEditClient}
                        className="flex-1 py-5 bg-slate-100 text-slate-500 font-bold rounded-2xl"
                      >
                        Descartar
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-5 bg-[#1b5e20] text-white font-black rounded-2xl text-lg shadow-xl hover:bg-green-900 transition-all"
                      >
                        Guardar Todos os Dados
                      </button>
                    </div>
                  </form>
                )}
             </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedClient && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[130] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600 sm:w-8 sm:h-8" size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-900">Eliminar Cliente</h2>
                <p className="text-slate-600 font-bold mt-1 text-sm sm:text-base">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            
            {(pendingDebt > 0 || clientMovements.length > 0 || clientTechJobs.length > 0) ? (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1 sm:w-6 sm:h-6" size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-amber-900 mb-2 text-sm sm:text-base">Este cliente tem registos de pagamentos ou movimentos:</p>
                    <ul className="space-y-2 text-amber-800 font-bold text-xs sm:text-sm">
                      {pendingDebt > 0 && (
                        <li>• Dívida pendente: {pendingDebt.toFixed(2)}€</li>
                      )}
                      {clientMovements.length > 0 && (
                        <li>• {clientMovements.length} movimento(s) registado(s)</li>
                      )}
                      {clientTechJobs.length > 0 && (
                        <li>• {clientTechJobs.length} assistência(s) técnica(s)</li>
                      )}
                    </ul>
                    <p className="mt-3 text-amber-900 font-black text-xs sm:text-sm">
                      Tem a certeza que deseja eliminar este cliente? Esta ação irá remover o cliente como se nunca tivesse existido.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border-2 border-red-200 rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-1 sm:w-6 sm:h-6" size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-red-900 mb-2 text-sm sm:text-base">
                      Tem a certeza absoluta que deseja eliminar o cliente <span className="text-red-700">{selectedClient.name}</span>?
                    </p>
                    <p className="text-red-800 font-bold text-xs sm:text-sm mt-2">
                      Esta ação é permanente e não pode ser desfeita. O cliente será removido completamente do sistema.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-4 sm:py-5 bg-slate-100 text-slate-600 font-black rounded-xl sm:rounded-2xl hover:bg-slate-200 transition-all text-sm sm:text-base"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDeleteClient(selectedClient.id)}
                className="flex-1 py-4 sm:py-5 bg-red-600 text-white font-black rounded-xl sm:rounded-2xl hover:bg-red-700 transition-all shadow-lg text-sm sm:text-base"
              >
                Sim, Eliminar Cliente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
